import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { Show } from '../../../components/Show';
import type { Nullable } from '../../../types/common/utility';
import { toUniversalDate } from '../../../utils/date.utils';

import RiskReportDoc from '../../../_assets/svg/RiskReportDoc.svg';
import CbButton from '../../../components/Buttons/CbButton';
import { OutlinedButton } from './RiskReportCard/components';

import { useAsyncEffectWithLoading } from './RiskReportCard/helpers';

import { usePersona } from '../../../components/hooks/usePersona';
import { useCowbellTranslations } from '../../../i18n/translations';

type RiskReportCardProps = {
  generateReport: () => Promise<void> | void;
  downloadReport: () => Promise<void> | void;
  redirectToAddDomain: () => void;
  generateDisabled?: boolean;
  downloadDisabled?: boolean;
  latestReportDate: Nullable<string>; // ISO date string of the latest generated report
  isRiskScannerRunning: boolean;
  isReportGenerating: boolean;
  accountHasDomain: boolean;
};

const RiskReportCard = (props: RiskReportCardProps) => {
  const { t } = useCowbellTranslations();
  const persona = usePersona();

  return (
    // elevation 6, like every other tile. A Card defaults to elevation 1, which
    // both themes map to no shadow at all, so this one sat flat on a page of
    // raised tiles.
    <Card elevation={6} sx={{ paddingY: '1.5rem' }}>
      <Grid container>
        <Grid item xs={1}>
          <Box display="flex" justifyContent="center">
            <RiskReportDoc style={{ fontSize: '5rem' }} />
          </Box>
        </Grid>

        <Grid
          item
          xs={7}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <CardHeader>Risk Report</CardHeader>
          <Show when={!props.isRiskScannerRunning}>
            <p style={{ margin: '4px 0' }}>
              This Cyber Risk Report is an overall evaluation of your&nbsp;
              {t('organization').toLowerCase()}'s risk profile presenting{' '}
              {t('individualized').toLowerCase()}
              &nbsp;findings with {t('personalized').toLowerCase()}{' '}
              recommendations for remediation.
            </p>
          </Show>

          <Show when={props.isRiskScannerRunning}>
            <p style={{ margin: '4px 0' }}>
              Once the risk scan of your account is complete, you will be able
              to access your Risk Report.{' '}
              <Show when={persona.isInsured}>
                We will send you an email notification as soon as the scan is
                finished.
              </Show>
            </p>
          </Show>
        </Grid>

        <RiskReportActions {...props} />
      </Grid>
    </Card>
  );
};

const RiskReportActions = (props: RiskReportCardProps) => {
  const formattedReportDate = toUniversalDate(props.latestReportDate);
  const theme = useTheme();

  const reportGeneration = useAsyncEffectWithLoading({
    effect: props.generateReport,
  });

  const handleGenerateClick = () => {
    reportGeneration.request();
  };

  return (
    <Grid item xs={4}>
      <Show when={props.accountHasDomain}>
        <ActionsContainer>
          <ActionContainer>
            <CbButton
              styleName="ctaButton"
              size="small"
              onClick={props.downloadReport}
              disabled={props.downloadDisabled || reportGeneration.isLoading}
            >
              Download
            </CbButton>

            <Show
              when={Boolean(props.latestReportDate)}
              fallback={<ActionHelperText>None generated</ActionHelperText>}
            >
              <ActionHelperText>
                Generated <em>{formattedReportDate}</em>
              </ActionHelperText>
            </Show>
          </ActionContainer>

          <ActionContainer>
            <OutlinedButton
              sx={{ width: '8rem' }}
              onClick={handleGenerateClick}
              disabled={reportGeneration.isLoading || props.generateDisabled}
              endIcon={
                reportGeneration.isLoading ? (
                  <CircularProgress size={20} color="secondary" />
                ) : null
              }
            >
              Generate
            </OutlinedButton>

            <ActionHelperText>
              <Show when={!props.isReportGenerating}>
                Generate Latest Risk Report
              </Show>

              <Show when={props.isReportGenerating}>
                <em>Generation in progress...</em>
              </Show>
            </ActionHelperText>
          </ActionContainer>
        </ActionsContainer>
      </Show>

      <Show when={!props.accountHasDomain}>
        <ActionsContainer sx={{ gap: '0' }}>
          <Box display="flex" alignItems="center" gap="5px">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              size="xl"
              color={theme.config.colors.chromeYellow}
            />
            <p
              style={{
                color: theme.config.colors.chromeYellow,
                fontStyle: 'italic',
              }}
            >
              Risk Report won't be generated as Primary Web Domain is missing.
            </p>
          </Box>
          <CbButton
            styleName="ctaButton"
            size="small"
            onClick={props.redirectToAddDomain}
          >
            Add
          </CbButton>
        </ActionsContainer>
      </Show>
    </Grid>
  );
};

export default RiskReportCard;

const CardHeader = styled(Typography)`
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
`;

const ActionsContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
  margin-right: 1rem;
  gap: 1rem;
`;

const ActionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const ActionHelperText = styled('span')`
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.secondary.main};
  text-align: center;
`;
